import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { CreditCard, Copy, LoaderCircle, QrCode, X } from 'lucide-react';
import { CheckoutPaymentPayload, CheckoutPaymentResult } from '../api';
import { FormState } from '../types';
import { formatCurrency, formatPhone, onlyNumbers } from '../utils';

interface PaymentCheckoutProps {
  form: FormState;
  baseValue: number;
  loading: boolean;
  error: string;
  payment: CheckoutPaymentResult | null;
  onClose: () => void;
  onPay: (payload: CheckoutPaymentPayload) => void;
  onFreeRegistration: (cupom: string) => void;
  onCheckPayment: () => void;
}

const cardGrossValue = (baseValue: number, installments: number) => {
  const rate = installments === 1 ? 0.0299 : 0.0349;
  return Number(((baseValue + 0.49) / (1 - rate)).toFixed(2));
};

const formatCpf = (value: string) =>
  onlyNumbers(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

const formatCard = (value: string) => onlyNumbers(value).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

export const PaymentCheckout = ({
  form,
  baseValue,
  loading,
  error,
  payment,
  onClose,
  onPay,
  onFreeRegistration,
  onCheckPayment
}: PaymentCheckoutProps) => {
  const [method, setMethod] = useState<'PIX' | 'CREDIT_CARD'>('PIX');
  const [installments, setInstallments] = useState(1);
  const [couponEnabled, setCouponEnabled] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [payer, setPayer] = useState({
    name: form.nomeMae || form.nomePai,
    cpf: '',
    email: '',
    phone: form.telefoneMae,
    postalCode: form.cep,
    address: form.endereco,
    addressNumber: form.numero,
    addressComplement: form.complemento,
    province: form.bairro
  });
  const [card, setCard] = useState({ holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '' });
  const displayedValue = couponApplied ? 0 : method === 'CREDIT_CARD' ? cardGrossValue(baseValue, installments) : baseValue;
  const installmentValue = displayedValue / installments;
  const isPaid = payment && ['CONFIRMED', 'RECEIVED'].includes(payment.status);

  useEffect(() => {
    if (!couponEnabled || couponCode.trim().toLowerCase() !== 'gratuidade') {
      setCouponApplied(false);
      setCouponLoading(false);
      return;
    }

    setCouponLoading(true);
    const timer = window.setTimeout(() => {
      setCouponApplied(true);
      setCouponLoading(false);
    }, 900);
    return () => window.clearTimeout(timer);
  }, [couponCode, couponEnabled]);

  const valid = useMemo(() => {
    if (couponApplied) return true;
    const payerValid = payer.name.trim() && onlyNumbers(payer.cpf).length === 11 && payer.email.includes('@') && onlyNumbers(payer.phone).length >= 10 && onlyNumbers(payer.postalCode).length === 8 && payer.addressNumber.trim();
    if (!payerValid) return false;
    if (method === 'PIX') return true;
    return card.holderName.trim() && onlyNumbers(card.number).length >= 13 && card.expiryMonth.length === 2 && card.expiryYear.length === 4 && card.ccv.length >= 3;
  }, [card, couponApplied, method, payer]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    if (couponApplied) {
      // Guarda qual cupom foi usado: e o que permite sortear entre quem usou.
      onFreeRegistration(couponCode.trim().toLowerCase());
      return;
    }
    onPay({
      baseValue,
      billingType: method,
      installments,
      registrationReference: `COLONIA-${Date.now()}`,
      description: `Inscrição Colônia de Férias - ${form.nomeCompleto}`,
      payer: { ...payer, cpf: onlyNumbers(payer.cpf), phone: onlyNumbers(payer.phone), postalCode: onlyNumbers(payer.postalCode) },
      card: method === 'CREDIT_CARD' ? { ...card, number: onlyNumbers(card.number) } : undefined
    });
  };

  return (
    <div className="checkout-overlay">
      {couponLoading && (
        <div className="coupon-loading-overlay" role="status" aria-live="polite">
          <LoaderCircle className="spin" size={42} />
          <strong>Validando cupom...</strong>
        </div>
      )}
      <div className="checkout-modal">
        <div className="checkout-header">
          <div><h2>Pagamento</h2><p>{form.nomeCompleto}</p></div>
          <button type="button" onClick={onClose} aria-label="Fechar"><X size={22} /></button>
        </div>

        {payment ? (
          <div className="checkout-result">
            {payment.billingType === 'PIX' && !isPaid && (
              <>
                <h3>Escaneie o QR Code</h3>
                {payment.pixQrCodeUrl && <img className="pix-qr-image" src={`data:image/png;base64,${payment.pixQrCodeUrl}`} alt="QR Code PIX" />}
                <div className="pix-copy-row">
                  <input readOnly value={payment.pixQrCode || ''} />
                  <button type="button" title="Copiar código PIX" onClick={() => navigator.clipboard.writeText(payment.pixQrCode || '')}><Copy size={18} /></button>
                </div>
                <p className="checkout-total">{formatCurrency(payment.value)}</p>
                <button type="button" className="checkout-primary" onClick={onCheckPayment} disabled={loading}>
                  {loading ? <LoaderCircle className="spin" size={18} /> : null} Já fiz o pagamento
                </button>
              </>
            )}
            {payment.billingType === 'CREDIT_CARD' && (
              <div className={isPaid ? 'payment-approved' : 'payment-pending'}>
                <CreditCard size={42} />
                <h3>{isPaid ? 'Pagamento aprovado' : 'Pagamento em análise'}</h3>
                <p>{formatCurrency(payment.value)}{payment.installmentCount && payment.installmentCount > 1 ? ` em ${payment.installmentCount}x` : ''}</p>
              </div>
            )}
            {isPaid && <button type="button" className="checkout-primary" onClick={onCheckPayment}>Concluir inscrição</button>}
            {error && <div className="checkout-error">{error}</div>}
          </div>
        ) : (
          <form className="checkout-form" onSubmit={submit}>
            <div className="coupon-section">
              <label className="coupon-toggle">
                <input
                  type="checkbox"
                  checked={couponEnabled}
                  onChange={(event) => {
                    setCouponEnabled(event.target.checked);
                    if (!event.target.checked) setCouponCode('');
                  }}
                />
                <span>Tenho um cupom de desconto</span>
              </label>
              {couponEnabled && (
                <label className="coupon-field">
                  Cupom
                  <input value={couponCode} onChange={(event) => setCouponCode(event.target.value)} placeholder="Digite o cupom" autoComplete="off" />
                </label>
              )}
              {couponApplied && <div className="coupon-success">Cupom aplicado. Inscrição gratuita.</div>}
            </div>

            {!couponApplied && (
              <>
                <div className="payment-method-tabs">
                  <button type="button" className={method === 'PIX' ? 'active' : ''} onClick={() => { setMethod('PIX'); setInstallments(1); }}><QrCode size={20} /> PIX</button>
                  <button type="button" className={method === 'CREDIT_CARD' ? 'active' : ''} onClick={() => setMethod('CREDIT_CARD')}><CreditCard size={20} /> Cartão</button>
                </div>

                <div className="checkout-fields two-columns">
                  <label>Nome do responsável<input value={payer.name} onChange={(e) => setPayer({ ...payer, name: e.target.value })} /></label>
                  <label>CPF<input inputMode="numeric" value={payer.cpf} onChange={(e) => setPayer({ ...payer, cpf: formatCpf(e.target.value) })} placeholder="000.000.000-00" /></label>
                  <label>E-mail<input type="email" value={payer.email} onChange={(e) => setPayer({ ...payer, email: e.target.value })} /></label>
                  <label>Telefone<input value={payer.phone} onChange={(e) => setPayer({ ...payer, phone: formatPhone(e.target.value) })} /></label>
                </div>

                {method === 'CREDIT_CARD' && (
                  <div className="card-fields">
                    <label>Nome no cartão<input value={card.holderName} onChange={(e) => setCard({ ...card, holderName: e.target.value.toUpperCase() })} /></label>
                    <label>Número do cartão<input inputMode="numeric" value={card.number} onChange={(e) => setCard({ ...card, number: formatCard(e.target.value) })} placeholder="0000 0000 0000 0000" /></label>
                    <div className="three-columns">
                      <label>Mês<input inputMode="numeric" maxLength={2} value={card.expiryMonth} onChange={(e) => setCard({ ...card, expiryMonth: onlyNumbers(e.target.value).slice(0, 2) })} placeholder="MM" /></label>
                      <label>Ano<input inputMode="numeric" maxLength={4} value={card.expiryYear} onChange={(e) => setCard({ ...card, expiryYear: onlyNumbers(e.target.value).slice(0, 4) })} placeholder="AAAA" /></label>
                      <label>CVV<input inputMode="numeric" maxLength={4} value={card.ccv} onChange={(e) => setCard({ ...card, ccv: onlyNumbers(e.target.value).slice(0, 4) })} /></label>
                    </div>
                    <label>Parcelamento
                      <select value={installments} onChange={(e) => setInstallments(Number(e.target.value))}>
                        {[1, 2, 3, 4].map((count) => {
                          const total = cardGrossValue(baseValue, count);
                          return <option key={count} value={count}>{count}x de {formatCurrency(total / count)} - {formatCurrency(total)}</option>;
                        })}
                      </select>
                    </label>
                  </div>
                )}
              </>
            )}

            <div className="checkout-summary">
              <span>Total</span>
              <strong>{formatCurrency(displayedValue)}</strong>
              {!couponApplied && method === 'CREDIT_CARD' && installments > 1 && <small>{installments}x de {formatCurrency(installmentValue)}</small>}
            </div>
            {error && <div className="checkout-error">{error}</div>}
            <button type="submit" className="checkout-primary" disabled={!valid || loading || couponLoading}>
              {loading && <LoaderCircle className="spin" size={18} />}
              {couponApplied ? 'Confirmar inscrição gratuita' : method === 'PIX' ? 'Gerar PIX' : 'Pagar com cartão'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
